import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

/**
 * LocalLogin Page - Hidden route for existing local users
 * Access via /tf-admin-auth (not visible in navigation)
 */
function LocalLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      let data = {};
      
      try {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text);
        }
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
      }

      if (response.ok) {
        // Check if admin MFA is required
        if (data.mfa_required) {
          sessionStorage.setItem('mfa_challenge_token', data.challenge_token);
          sessionStorage.setItem('mfa_email', data.email || email);
          navigate('/mfa-verify', { replace: true });
          return;
        }
        // Admin without MFA → login then redirect to setup
        if (data.mfa_setup_required) {
          toast.success('Login successful! Please set up two-factor authentication to secure your account.');
          // Set token in localStorage BEFORE navigation to avoid React render race condition
          localStorage.setItem('token', data.token);
          // Use full page navigation to ensure token is available when MFASetup renders
          window.location.replace('/mfa-setup');
          return;
        }
        toast.success('Login successful!');
        onLogin(data.user, data.token);
        // Redirect to the originally requested URL, fallback to /browse
        const returnUrl = sessionStorage.getItem('returnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl');
          navigate(returnUrl);
        } else {
          navigate('/browse');
        }
      } else {
        const errorMessage = data.detail || data.message || 
          (response.status === 401 ? 'Invalid email or password.' : 
           response.status === 404 ? 'Account not found.' :
           'Login failed. Please check your credentials.');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#1B5E5F',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <header style={{ 
        backgroundColor: '#1B5E5F', 
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        padding: '16px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/technofocus-logo.png" alt="Technofocus" style={{ height: '20px', width: 'auto', filter: 'brightness(0) invert(1)' }} />
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px', fontWeight: '200' }}>|</span>
            <img src="/levelup-logo-light.svg" alt="LevelUp" style={{ height: '20px', width: 'auto' }} />
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '20px', fontWeight: '200' }}>|</span>
            <img src="/ms-levelup-logo.svg" alt="Sponsored by Microsoft" style={{ height: '28px', width: 'auto' }} />
          </div>
          <span style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: '600',
            padding: '4px 10px',
            borderRadius: '12px',
            textTransform: 'uppercase'
          }}>
            Local Login
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '40px 24px'
      }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>
          {/* Back Button */}
          <button
            onClick={() => navigate('/login')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#FFFFFF',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '24px',
              opacity: 0.8
            }}
          >
            <ArrowLeft size={18} />
            Back to SSO Login
          </button>

          {/* Login Card */}
          <div style={{ 
            backgroundColor: '#FFFFFF', 
            borderRadius: '12px', 
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '40px 32px' }}>
              <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                  Local Account Login
                </h1>
                <p style={{ fontSize: '14px', color: '#6B7280' }}>
                  For existing local accounts only
                </p>
              </div>

              {/* Warning Notice */}
              <div style={{
                backgroundColor: '#FEF3C7',
                border: '1px solid #F59E0B',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '24px'
              }}>
                <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>
                  <strong>Note:</strong> This login is for existing local accounts. New users should sign in via LevelUp SSO.
                </p>
              </div>

              <form onSubmit={handleSubmit} data-testid="local-login-form">
                <div style={{ marginBottom: '20px' }}>
                  <label 
                    htmlFor="email-input"
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}
                  >
                    Email Address
                  </label>
                  <input
                    id="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    data-testid="local-login-email"
                    placeholder="your.email@company.com"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#1F2937',
                      backgroundColor: '#FFFFFF',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1B5E5F';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label 
                    htmlFor="password-input"
                    style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}
                  >
                    Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      data-testid="local-login-password"
                      placeholder="Enter your password"
                      style={{
                        width: '100%',
                        padding: '12px 44px 12px 16px',
                        border: '2px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#1F2937',
                        backgroundColor: '#FFFFFF',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1B5E5F';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#D1D5DB';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6B7280',
                        padding: '4px'
                      }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="local-login-submit"
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: loading ? '#6B7280' : '#0C6A1D',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </div>
          </div>

          <p style={{ 
            textAlign: 'center', 
            marginTop: '24px', 
            fontSize: '12px', 
            color: '#B2DFDB'
          }}>
            Local authentication for existing accounts
          </p>
        </div>
      </main>
    </div>
  );
}

export default LocalLogin;
